/**
 * Định dạng số theo chuẩn Việt Nam: dấu chấm ngăn hàng nghìn, dấu phẩy ngăn phần thập phân
 * (M16 §0.4 · M17 §5.3 · M18 UI-05).
 *
 * Quy tắc xuyên suốt: thiếu dữ liệu hiện dấu gạch, KHÔNG hiện 0. `0` là giá trị nghiệp vụ
 * thật (cutting_length = 0 cho 0 giây hợp lệ — BR-M16-45) nên gộp hai thứ này là làm sai
 * báo cáo (BR-M16-12 / RB-04).
 */
import {RATE_MAX_DECIMALS} from '../constants'

export const DASH = '—'

const isBlank = (value) => value === null || value === undefined || value === ''

/** Số nguyên có ngăn cách hàng nghìn. `1093` → `1.093`. */
export const formatInt = (value) => {
    if (isBlank(value) || Number.isNaN(Number(value))) return DASH

    return Number(value).toLocaleString('vi-VN', {maximumFractionDigits: 0})
}

/** Số thập phân cố định `digits` chữ số. `2.54` → `2,54`. */
export const formatDecimal = (value, digits = 2) => {
    if (isBlank(value) || Number.isNaN(Number(value))) return DASH

    return Number(value).toLocaleString('vi-VN', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    })
}

/**
 * Số chữ số thập phân của chỉ số tiêu chuẩn sau khi làm tròn — lấy thẳng từ `constants/`, KHÔNG khai
 * lại ở đây.
 *
 * ⚠️ Trước đây file này có hằng `RATE_DECIMALS = 0` riêng, còn `constants/RATE_MAX_DECIMALS` thì
 *    không ai dùng. Hệ quả: sửa `RATE_MAX_DECIMALS` cho khớp service **không đổi được gì**, trong khi
 *    docblock của nó lại dặn "đổi ở service thì phải đổi ở đây". Hai hằng cho một luật, và cái được
 *    sửa là cái chết. Nay chỉ còn một nguồn: `constants/index.js`, khớp `RATE_MAX_DECIMALS` của service.
 *
 * Đổi nó là đổi đồng loạt `ceilTo` (mặc định), `roundRate` (ghi thật khi rời ô nhập) và `formatRate`
 * (hiển thị ở 6 màn) — cố ý gom về một nơi để không chỗ nào làm tròn lệch chỗ khác.
 */
export const RATE_DECIMALS = RATE_MAX_DECIMALS

/**
 * Làm tròn LÊN tới `digits` chữ số thập phân.
 *
 * ⚠️ Không viết thẳng `Math.ceil(value * factor) / factor`. Nhân số thực hay sinh sai số: `1.1 * 10`
 *    ra `11.000000000000002`, `Math.ceil` nhảy lên 12 và kết quả thành `1.2` — sai hẳn một nấc dù
 *    con số vốn đã tròn. Phải gạt sai số đi trước khi làm tròn lên.
 *
 *    Với `digits = 0` thì `factor = 1`, bước gạt sai số vẫn cần: `Math.ceil(3.0000000000000004)`
 *    ra 4 thay vì 3.
 */
export const ceilTo = (value, digits = RATE_DECIMALS) => {
    if (isBlank(value) || Number.isNaN(Number(value))) return value

    const factor = 10 ** digits
    const scaled = Math.round(Number(value) * factor * 1e9) / 1e9

    return Math.ceil(scaled) / factor
}

/**
 * Chỉ số tiêu chuẩn: LÀM TRÒN LÊN thành SỐ NGUYÊN.
 *
 * Làm tròn lên chứ không làm tròn gần nhất: đây là định mức thời gian, ước thiếu thì kế hoạch
 * năng lực bị hụt giờ, ước thừa chỉ là dự phòng.
 */
export const formatRate = (value) => {
    if (isBlank(value) || Number.isNaN(Number(value))) return DASH

    return Number(ceilTo(value)).toLocaleString('vi-VN', {maximumFractionDigits: RATE_DECIMALS})
}

/** Giờ công: 1 chữ số lẻ ở M17, 2 chữ số lẻ ở M18. */
export const formatHours = (value, digits = 1) => {
    if (isBlank(value)) return DASH

    return formatDecimal(value, digits)
}

/**
 * Tỷ lệ 0→1 thành phần trăm. `null` → dấu gạch, KHÔNG phải 0% và cũng không 100% (BR-A13):
 * mẫu số bằng 0 nghĩa là "không áp dụng", khác hẳn "đạt 0%".
 */
export const formatPercent = (ratio, digits = 0) => {
    if (isBlank(ratio)) return DASH

    return `${formatDecimal(Number(ratio) * 100, digits)}%`
}

/**
 * Hiệu suất — phần trăm **1 chữ số thập phân** (BR-3.2 của SRS M18).
 *
 * ⚠️ Đây là một hàm riêng chứ không phải `formatPercent(x, 1)` rải khắp nơi, vì con số này phải
 *    khớp với HẠNG hiện ngay cạnh nó. Làm tròn 0 chữ số thì 89,6% in ra "90%" trong khi ngưỡng
 *    hạng A là 90% và người đó đang là hạng B — một dòng tự mâu thuẫn, và là dòng người dùng mang
 *    đi hỏi lại.
 *
 * ⚠️ KHÔNG dùng cho NGƯỠNG (chân bộ lọc "A ≥ 90% · B ≥ 75%"). Ngưỡng là con số cấu hình, vốn
 *    tròn; in "A ≥ 90,0%" là thêm nhiễu vào đúng chỗ cần đọc nhanh. Chỉ số ĐO ĐƯỢC mới cần chữ số
 *    lẻ, vì chỉ số đo được mới rơi sát ngưỡng.
 */
export const formatEfficiency = (ratio) => formatPercent(ratio, 1)

/** Số có dấu, dùng cho cột Thiếu / thừa người. Dương là thiếu, âm là thừa. */
export const formatSigned = (value) => {
    if (isBlank(value)) return DASH

    const number = Number(value)

    return `${number > 0 ? '+' : ''}${formatInt(number)}`
}

const pad = (value) => String(value).padStart(2, '0')

const toDate = (value) => {
    if (!value) return null

    const date = value instanceof Date ? value : new Date(value)

    return Number.isNaN(date.getTime()) ? null : date
}

/** `DD/MM/YYYY` — định dạng ngày duy nhất được dùng trên giao diện (M16 §0.4). */
export const formatDate = (value) => {
    const date = toDate(value)

    if (!date) return DASH

    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`
}

/**
 * Nhãn múi giờ của TRÌNH DUYỆT đang mở màn hình — `GMT+7`, `GMT-5`, `GMT+5:30`.
 *
 * ⚠️ Lấy từ trình duyệt chứ KHÔNG ghi cứng `GMT+7`. Mọi mốc thời gian trên màn đều được định dạng
 *    theo giờ máy người xem (`formatDateTime` dùng `getHours()` địa phương), nên dán nhãn cứng là
 *    nói dối đúng những người cần nhãn nhất: ai mở báo cáo từ múi giờ khác sẽ thấy giờ của họ dưới
 *    nhãn GMT+7 và đọc sai toàn bộ khoảng lọc.
 *
 * ⚠️ `getTimezoneOffset()` trả số PHÚT phải CỘNG vào giờ địa phương để ra UTC, nên dấu của nó NGƯỢC
 *    với cách người ta viết múi giờ: ở Việt Nam nó trả `-420`, phải đảo dấu mới ra `+7`.
 *
 * ⚠️ PHẢI truyền `at` là chính mốc đang hiển thị. Offset phụ thuộc THỜI ĐIỂM ở mọi vùng có DST:
 *    Berlin là GMT+1 vào tháng 1 và GMT+2 vào tháng 7. Gọi `timezoneLabel()` rỗng rồi dán lên một
 *    mốc của kỳ khác là lệch đúng 1 giờ — và ở biên ngày là lệch cả một ngày sản lượng. Việt Nam
 *    không có DST nên lỗi này KHÔNG BAO GIỜ lộ trên máy dev, trong khi nhãn này sinh ra chính vì
 *    người xem ở múi giờ khác. Dùng `timezoneLabelForRange` cho một khoảng.
 */
export const timezoneLabel = (at = new Date()) => {
    const offset = -at.getTimezoneOffset()
    const sign = offset < 0 ? '-' : '+'
    const abs = Math.abs(offset)
    const minutes = abs % 60

    // Chỉ in phút khi khác 0: phần lớn múi giờ tròn giờ, `GMT+7:00` chỉ làm dài dòng.
    return `GMT${sign}${Math.floor(abs / 60)}${minutes ? `:${pad(minutes)}` : ''}`
}

/**
 * Chuẩn hoá một giá trị thành thứ đọc được offset: `Date` hợp lệ, hoặc `null`.
 *
 * Nhận `Date`, chuỗi ISO, `moment`, và cả thứ TỰ BIẾT offset của mình — vế cuối để ca "hai đầu khác
 * kỳ DST" kiểm được mà không phải bắt cả CI chạy dưới một `TZ=` cố định (đổi `process.env.TZ` giữa
 * chừng thì V8 đã cache, không ăn).
 *
 * ⚠️ `new Date('rác')` CŨNG là `Date` và CŨNG có `getTimezoneOffset` — nó trả `NaN`. Nhận bừa theo
 *    kiểu duck-typing là đi vòng qua đúng chốt kiểm `NaN` của `toDate`, và nhãn in ra `GMT+NaN`
 *    ngay cạnh một mốc đã hiện `—`.
 */
const asOffsetSource = (value) => {
    if (value && typeof value.getTimezoneOffset === 'function') {
        return Number.isNaN(value.getTimezoneOffset()) ? null : value
    }

    return toDate(value)
}

/**
 * Mốc này có đọc được offset không — dùng để quyết định CÓ in nhãn múi giờ hay không.
 *
 * ⚠️ Có hàm này vì `timezoneLabelAt` phải trả một nhãn nào đó, và nhãn đường lui là nhãn của MÁY.
 *    Dán nhãn máy cạnh một mốc đã hiện `—` là nói dối kiểu im lặng: `GMT+NaN` xấu nhưng tự tố cáo,
 *    còn `(GMT+13:45)` trông hoàn toàn bình thường trong khi chẳng có mốc nào. Call-site phải tự
 *    quyết định, và đây là cách hỏi.
 */
export const hasReadableOffset = (value) => asOffsetSource(value) !== null

/** Nhãn múi giờ tại ĐÚNG một mốc, chịu được giá trị rỗng hoặc không parse được. */
export const timezoneLabelAt = (value) => timezoneLabel(asOffsetSource(value) || undefined)

/**
 * Nhãn múi giờ cho một KHOẢNG `from → to`.
 *
 * Hai đầu khoảng có thể rơi vào hai kỳ DST khác nhau (lọc 25/03 → 05/04 ở châu Âu là một ca thật),
 * và khi đó KHÔNG có một nhãn nào đúng cho cả hai. Nói ra cả hai còn hơn chọn bừa một đầu rồi khiến
 * nửa khoảng bị đọc lệch một giờ.
 */
export const timezoneLabelForRange = (from, to) => {
    const start = asOffsetSource(from)
    const end = asOffsetSource(to)

    /*
     * ⚠️ Đầu nào không đọc được thì lấy nhãn của đầu KIA, tuyệt đối không thay bằng `new Date()`.
     *    Thay bằng "bây giờ" sinh ra nhãn kép `GMT+13:45 → GMT+12:45` cho một khoảng chỉ có một
     *    đầu, trong đó đầu thứ hai không phải dữ liệu nào cả — đúng kiểu "dán offset của hôm nay
     *    lên mốc của kỳ khác" mà hàm này sinh ra để diệt.
     */
    if (!start && !end) return timezoneLabel()
    if (!start || !end) return timezoneLabel(start || end)

    const startLabel = timezoneLabel(start)
    const endLabel = timezoneLabel(end)

    return startLabel === endLabel ? startLabel : `${startLabel} → ${endLabel}`
}

/** `DD/MM/YYYY HH:mm` — dùng cho thời điểm tính và thời gian log. */
export const formatDateTime = (value) => {
    const date = toDate(value)

    if (!date) return DASH

    return `${formatDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Giờ Việt Nam là UTC+7 CỐ ĐỊNH — không có DST, nên một hằng số là đủ và luôn đúng. */
const VN_OFFSET_MS = 7 * 60 * 60 * 1000

/**
 * Các thành phần ngày/giờ của một mốc, đọc theo đồng hồ VIỆT NAM.
 *
 * ⚠️ Dịch mốc rồi đọc bằng getter `UTC*`, KHÔNG dùng `Intl` với `timeZone: 'Asia/Ho_Chi_Minh'`:
 *    bản Node dựng test không chắc có đủ dữ liệu múi giờ (ICU rút gọn), và khi thiếu thì `Intl`
 *    NÉM `RangeError` chứ không rơi về mặc định — một màn hình trắng ở đúng chỗ chỉ in hai con số.
 *    Việt Nam không có DST nên phép dịch cố định này cho kết quả y hệt mà không phụ thuộc gì.
 *    Cùng quy ước với `vnDayOf`/`vnDayStart` phía service (`UTC+7 cố định`).
 */
const vnPartsOf = (date) => {
    const shifted = new Date(date.getTime() + VN_OFFSET_MS)

    return {
        day: shifted.getUTCDate(),
        month: shifted.getUTCMonth() + 1,
        year: shifted.getUTCFullYear(),
        hours: shifted.getUTCHours(),
        minutes: shifted.getUTCMinutes(),
    }
}

/**
 * `HH:mm` theo giờ VIỆT NAM.
 *
 * ⚠️ Dùng cho mọi mốc in trong phụ đề báo cáo M18. Phụ đề đó mở đầu bằng `(ngày giờ Việt Nam)`, nên
 *    in tiếp hai con số theo đồng hồ của MÁY là tự mâu thuẫn: người xem từ UTC+0 đọc "số liệu đủ
 *    tới 10:05" và trừ đi bảy tiếng so với mọi con số khác trên màn hình. (Vòng review 2, G-R7.)
 */
export const formatVnTime = (value) => {
    const date = toDate(value)

    if (!date) return DASH

    const parts = vnPartsOf(date)

    return `${pad(parts.hours)}:${pad(parts.minutes)}`
}

/** `DD/MM/YYYY HH:mm` theo giờ VIỆT NAM — cặp song sinh của `formatVnTime`. */
export const formatVnDateTime = (value) => {
    const date = toDate(value)

    if (!date) return DASH

    const parts = vnPartsOf(date)

    return `${pad(parts.day)}/${pad(parts.month)}/${parts.year} ${pad(parts.hours)}:${pad(parts.minutes)}`
}

/**
 * Giây → `Xh YYm ZZs`, BỎ đơn vị lớn hơn khi bằng 0 (không hiện `0h`, và khi không có giờ thì phút
 * không đệm số 0 — `3m 08s`, không phải `03m 08s`) — cột Giờ định mức của SCR-M18-002. Giây LUÔN đệm
 * 2 chữ số vì luôn đứng sau một đơn vị khác (trừ khi cả giờ lẫn phút đều 0, chỉ còn `Zs` trần).
 *
 * Làm tròn GẦN NHẤT tới giây nguyên trước khi tách — đây là hiển thị, không phải giá trị ghi xuống
 * DB, nên không cần làm tròn LÊN như `formatRate`/`ceilTo` (đó là luật riêng cho lúc GHI).
 */
export const formatDurationHMS = (seconds) => {
    if (isBlank(seconds) || Number.isNaN(Number(seconds))) return DASH

    const total = Math.round(Number(seconds))
    const h = Math.floor(total / 3600)
    const m = Math.floor((total % 3600) / 60)
    const s = total % 60

    if (h > 0) return `${h}h ${pad(m)}m ${pad(s)}s`
    if (m > 0) return `${m}m ${pad(s)}s`

    return `${s}s`
}

/** `HH:mm` — dùng cho cột Khoảng thời gian của SCR-M18-003. */
export const formatTime = (value) => {
    const date = toDate(value)

    if (!date) return DASH

    return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Khoảng hiệu lực còn hạn hiện `01/09/2026 →` (M16 §0.4). */
export const formatEffectiveRange = (from, to) => {
    if (!to) return `${formatDate(from)} →`

    return `${formatDate(from)} → ${formatDate(to)}`
}

/**
 * Ngày làm việc kế tiếp — mặc định của ô Hiệu lực từ (G.23) và của nhóm mới (N1).
 * Chưa có master ngày nghỉ nên tạm coi mọi ngày là ngày làm việc; đổi ở đây khi có master.
 */
export const nextWorkingDay = (base = new Date()) => {
    const date = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 1)

    return date
}

/**
 * Chỉ số ĐÚNG NHƯ ĐANG LƯU — không làm tròn.
 *
 * Dùng cho màn Lịch sử. `formatRate` làm tròn LÊN, hợp lý cho cấu hình đang chạy (số sẽ-được-ghi),
 * nhưng SAI cho lịch sử: service cố ý không backfill bản đã đóng khoảng, nên bản ghi 0,4 vẫn là 0,4.
 * In nó thành "1" là làm hỏng đúng câu hỏi mà màn Lịch sử tồn tại để trả lời.
 */
export const formatExactRate = (value) => {
    if (isBlank(value) || Number.isNaN(Number(value))) return DASH

    return Number(value).toLocaleString('vi-VN', {maximumFractionDigits: 4})
}

/**
 * Làm tròn LÊN thành SỐ NGUYÊN khi rời ô nhập (V-05: tự làm tròn, không báo lỗi).
 *
 * Đây là làm tròn GHI THẬT, không phải chỉ hiển thị: rời ô là giá trị trong form đổi luôn, bấm Lưu
 * là xuống DB đúng con số đó. Khớp đúng `ceilRate` của service (`RATE_MAX_DECIMALS = 0`, làm tròn
 * LÊN), nên giá trị hiện trên màn hình chính là giá trị sẽ được ghi — không còn "gõ 0,4, lưu ra 1".
 *
 * ⚠️ Đường NHẬP EXCEL KHÔNG đi qua đây: `commitImport` gửi lại chính file gốc cho service, nên giá
 *    trị lưu xuống là do service quyết. Service phải áp cùng quy tắc làm tròn lên số nguyên, nếu
 *    không thì chỉ số nhập bằng Excel sẽ lệch với chỉ số nhập tay.
 */
export const roundRate = (value) => ceilTo(value)

/** Phần trăm lệch giữa chỉ số mới và chỉ số cũ, dùng cho ngưỡng ±30% (VAL-05). */
export const rateDeltaPercent = (oldRate, newRate) => {
    if (isBlank(oldRate) || Number(oldRate) === 0) return null

    return ((Number(newRate) - Number(oldRate)) / Number(oldRate)) * 100
}

/**
 * Lượt sửa chỉ số này có phải hỏi lý do không (VAL-05)?
 *
 * ⚠️ Mốc 0 PHẢI xử riêng, và đây là hàm tồn tại chỉ vì lý do đó. `rateDeltaPercent` trả `null` khi
 *    chỉ số cũ là 0 — đúng về toán, vì phần trăm lệch so với 0 là vô định — nhưng caller đọc `null`
 *    thành "không đáng kể" thì một nhóm đang khai 0 giây sửa lên BẤT KỲ số nào cũng lọt qua cửa hỏi
 *    lý do. Mà đó là thay đổi kịch tính nhất có thể: từ "không đi qua công đoạn" thành có định mức
 *    thật, đổi giờ định mức của mọi SKU nhóm đó phủ.
 *
 *    Trước khi cho khai 0, V-04 đòi `rate > 0` nên `oldRate === 0` không tồn tại được và nhánh này
 *    là code chết — mở khoá 0 làm nó sống dậy. Chiều ngược lại (`500 → 0`) vẫn bị hỏi vì lệch
 *    −100%; bất đối xứng đúng ở chiều nguy hiểm là lý do phải viết hẳn ra thay vì tin vào `null`.
 */
export const rateChangeNeedsReason = (oldRate, newRate, thresholdPercent) => {
    if (isBlank(oldRate)) return false

    if (Number(oldRate) === 0) return Number(newRate) !== 0

    const delta = rateDeltaPercent(oldRate, newRate)

    return delta !== null && Math.abs(delta) > thresholdPercent
}

/**
 * Ngày người dùng chọn → mốc ISO gửi lên service. Nửa đêm **UTC của đúng ngày đó**.
 *
 * ⚠️ KHÔNG dùng `value.startOf('day').toISOString()`: đó là nửa đêm giờ VN (+07), tức 17:00Z của NGÀY
 *    HÔM TRƯỚC. Service cắt mốc "hôm nay" theo UTC (`Date.UTC(...)`) và in lại `effectiveFrom` đã cắt
 *    10 ký tự đầu, nên chọn đúng hôm nay vẫn nhận cảnh báo "Ngày hiệu lực <hôm qua> ở quá khứ" — vừa
 *    báo sai, vừa in ra một ngày người dùng chưa từng chọn. Cảnh báo nhiễu lặp lại mỗi lần lưu thì
 *    người dùng học cách bỏ qua cả những cảnh báo thật.
 *
 * `effectiveFrom`/`effectiveTo` là NGÀY chứ không phải mốc thời gian, nên quy ước này khớp luôn với
 * đường import: ô `2026-09-11` trong CSV được service đọc thành `2026-09-11T00:00:00.000Z`.
 */
export const dateOnlyIso = (value) => (value ? `${value.format('YYYY-MM-DD')}T00:00:00.000Z` : null)


/**
 * Phần trăm của `part` trên `total`, dạng chuỗi đã kèm dấu `%`.
 *
 * ⚠️ Khác biệt đáng giá: giá trị LỚN HƠN 0 nhưng làm tròn về 0 sẽ hiện `~0%`, không phải `0,0%`.
 *
 * Một dòng ghi "1 lần làm lại · 0,0%" tự mâu thuẫn — người đọc hoặc tưởng con số bên trên sai, hoặc
 * tưởng tỷ lệ đúng bằng không rồi bỏ qua. `~0%` nói đúng thứ đang xảy ra: có, nhưng nhỏ hơn mức mà
 * một chữ số thập phân diễn tả được.
 */
export const percentOf = (part, total, digits = 1) => {
    if (!total) return DASH

    const ratio = (Number(part) / Number(total)) * 100

    if (!Number.isFinite(ratio)) return DASH

    if (ratio > 0 && Number(ratio.toFixed(digits)) === 0) return '~0%'

    return `${formatDecimal(ratio, digits)}%`
}
